import { useEffect, useState } from "react";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const monthOptions = (() => {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 18; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
    out.push({ iso, label: d.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) });
  }
  return out;
})();

const REPORTS = [
  { key: "bravura-salary-sheet", label: "Bravura Salary Sheet", desc: "Legacy template format — Identity / Rate / Attendance / Earnings / Deductions / Employer side-by-side, one row per employee.", needsMonth: true, featured: true },
  { key: "salary-register", label: "Salary Register", desc: "Full breakdown of all approved payslips with earnings, deductions, employer cost.", needsMonth: true },
  { key: "pf-register", label: "PF Register (EPFO ECR)", desc: "UAN, Basic, EPF/EPS/EDLI columns formatted for EPFO ECR upload.", needsMonth: true },
  { key: "esi-register", label: "ESI Register", desc: "ESI Code, Gross, Employee + Employer ESI for ESIC return.", needsMonth: true },
  { key: "tds-register", label: "TDS Register", desc: "PAN, Gross, TDS deducted per employee.", needsMonth: true },
  { key: "ot-register", label: "OT Register (Blue collar)", desc: "OT minutes / hours / days / earning for blue-collar employees.", needsMonth: true },
  { key: "late-register", label: "Late Entry Register", desc: "Late count + days deducted (Admin only).", needsMonth: true },
  { key: "attendance-register", label: "Attendance Register", desc: "Daily P/H/A grid for the month.", needsMonth: true },
  { key: "headcount-movement", label: "Headcount Movement", desc: "Joiners / leavers / rejoiners list across all months.", needsMonth: false },
];

const BANK_FORMATS = [
  { value: "hdfc", label: "HDFC Bank" },
  { value: "icici", label: "ICICI Bank" },
  { value: "sbi", label: "State Bank of India" },
  { value: "axis", label: "Axis Bank" },
];

export default function Reports() {
  const [units, setUnits] = useState([]);
  const [unitId, setUnitId] = useState("");
  const [month, setMonth] = useState(monthOptions[0].iso);
  const [bank, setBank] = useState("hdfc");

  useEffect(() => {
    api.get("/units").then(({ data }) => setUnits(data));
  }, []);

  const downloadReport = async (key, extraParams = {}) => {
    try {
      const params = { ...extraParams };
      const conf = REPORTS.find((r) => r.key === key);
      if (conf?.needsMonth) params.month = month;
      if (unitId) params.unit_id = unitId;
      const res = await api.get(`/reports/${key}`, { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="(.+)"/);
      a.download = m ? m[1] : `${key}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      // Defensive parsing — when responseType is "blob", a 4xx body is also a Blob.
      // Read it as text and try JSON; fall back to status / generic message.
      let msg = "Report failed";
      try {
        const data = e?.response?.data;
        if (data && typeof data.text === "function") {
          const text = await data.text();
          if (text) {
            try {
              const parsed = JSON.parse(text);
              msg = parsed.detail || parsed.message || msg;
            } catch {
              msg = text.slice(0, 200);
            }
          }
        } else if (typeof data === "string") {
          msg = data.slice(0, 200);
        } else if (e?.response?.status) {
          msg = `Report failed (${e.response.status})`;
        } else if (e?.message) {
          msg = e.message;
        }
      } catch {
        // swallow — we'll just show the generic msg
      }
      toast.error(msg);
    }
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Phase 4</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
          Reports
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Statutory + management reports as Excel files. All exports are audit-logged.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Month</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="rpt-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              {monthOptions.map((m) => <SelectItem key={m.iso} value={m.iso}>{m.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-slate-500">Unit</Label>
          <Select value={unitId || "all"} onValueChange={(v) => setUnitId(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="rpt-unit"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All units</SelectItem>
              {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {REPORTS.map((r) => (
          <div
            key={r.key}
            className={`bg-white rounded-md p-4 flex items-start gap-3 ${
              r.featured
                ? "border-2 border-emerald-500 ring-1 ring-emerald-200 shadow-sm"
                : "border border-slate-200"
            }`}
          >
            <FileSpreadsheet className={`h-6 w-6 flex-shrink-0 mt-0.5 ${r.featured ? "text-emerald-600" : "text-emerald-600"}`} />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 text-sm flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
                {r.label}
                {r.featured && (
                  <span className="text-[9px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                    Recommended
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">{r.desc}</p>
              <Button
                size="sm"
                variant={r.featured ? "default" : "outline"}
                data-testid={`rpt-${r.key}`}
                onClick={() => downloadReport(r.key)}
                className={`mt-3 ${r.featured ? "brand-bg brand-bg-hover text-white" : ""}`}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download
              </Button>
            </div>
          </div>
        ))}

        {/* Bank file (special — bank format selector) */}
        <div className="bg-white border border-slate-200 rounded-md p-4 flex items-start gap-3">
          <FileSpreadsheet className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
              Bank Transfer File
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Net salary disbursement file in your bank's bulk-upload format.
            </p>
            <div className="flex gap-2 mt-3">
              <Select value={bank} onValueChange={setBank}>
                <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BANK_FORMATS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                data-testid="rpt-bank-file"
                onClick={() => downloadReport("bank-file", { format: bank })}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Download
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
